import {
    Fragment,
    useEffect,
    useState,
    type MouseEvent,
    type ReactNode,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { Badge, ClickAwayListener, Popper, useTheme } from '@mui/material';
import {
    NotificationsNone as NotificationsIcon,
    PersonOutline as PersonIcon,
    ChatBubbleOutline as ChatIcon,
    Menu as MenuIcon,
} from '@mui/icons-material';
import AppBar from '@/components/ui/AppBar/AppBar';
import Box from '@/components/ui/Box/Box';
import IconButton from '@/components/ui/IconButton/IconButton';
import Typography from '@/components/ui/Typography/Typography';
import { useAuth } from '@/providers/AuthProvider/AuthProvider';
import { useCompany } from '@/providers/CompanyProvider/CompanyProvider';
import { useNotifications } from '@/providers/NotificationsProvider/NotificationsProvider';
import fullLogo from '@/assets/images/logo_full.svg';
import ChannelsPopover from '../ChannelsPopover/ChannelsPopover';
import NotificationsPopover from '../NotificationsPopover/NotificationsPopover';
import { styles } from './styles';

type PopperKey = 'notifications' | 'chat' | 'user';

const UserPopup = () => {
    const auth = useAuth();
    const navigate = useNavigate();
    const theme = useTheme();
    return (
        <Box sx={styles.userMenu}>
            <Box
                sx={styles.userMenuItem(theme)}
                onClick={() => {
                    void auth.actions.logout();
                    navigate('/login');
                }}
            >
                Logout
            </Box>
        </Box>
    );
};

interface AppToolbarProps {
    showMenuButton?: boolean;
    onMenuClick?: () => void;
}

const AppToolbar = ({ showMenuButton, onMenuClick }: AppToolbarProps = {}) => {
    const theme = useTheme();
    const companyContext = useCompany();
    const notificationsContext = useNotifications();
    const companyName = companyContext.state.company?.name;
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
    const [openedPopper, setOpenedPopper] = useState<PopperKey | null>(null);

    useEffect(() => {
        void notificationsContext.actions.refreshNotifications();
    }, []);

    const unreadCount = notificationsContext.state.notifications.filter((n) => !n.read).length;
    const badgeContent =
        unreadCount < 1 ? null : unreadCount > 9 ? '9+' : unreadCount;

    const togglePopper = (key: PopperKey, e: MouseEvent<HTMLElement>) => {
        if (openedPopper === key) {
            setAnchorEl(null);
            setOpenedPopper(null);
            return;
        }
        setAnchorEl(e.currentTarget);
        setOpenedPopper(key);
    };

    const closePopper = () => {
        setAnchorEl(null);
        setOpenedPopper(null);
    };

    const notifIcon: ReactNode = badgeContent ? (
        <Badge color="secondary" badgeContent={badgeContent} showZero={false} sx={styles.badge}>
            <NotificationsIcon />
        </Badge>
    ) : (
        <NotificationsIcon />
    );

    const poppers: { id: PopperKey; element: ReactNode; iconElement: ReactNode }[] = [
        { id: 'notifications', element: <NotificationsPopover />, iconElement: notifIcon },
        { id: 'chat', element: <ChannelsPopover />, iconElement: <ChatIcon /> },
        { id: 'user', element: <UserPopup />, iconElement: <PersonIcon /> },
    ];

    return (
        <AppBar position="fixed">
            <Box sx={styles.container}>
                <Box sx={styles.logoBox}>
                    {showMenuButton && (
                        <IconButton
                            sx={styles.menuButton}
                            onClick={onMenuClick}
                            aria-label="Open navigation menu"
                        >
                            <MenuIcon />
                        </IconButton>
                    )}
                    <img alt="Repair Exchange" style={styles.logoImg} src={fullLogo} />
                    {companyName && (
                        <Typography variant="subtitle1" noWrap sx={styles.companyName}>
                            {companyName}
                        </Typography>
                    )}
                </Box>
                <Box sx={styles.iconsBox}>
                    <Box sx={styles.iconsContainer}>
                        {poppers.map((p) => (
                            <Fragment key={p.id}>
                                <IconButton
                                    sx={styles.headerIconBtn(theme)}
                                    onClick={(e) => togglePopper(p.id, e)}
                                >
                                    {p.iconElement}
                                </IconButton>
                                <Popper
                                    open={openedPopper === p.id}
                                    anchorEl={anchorEl}
                                    placement="bottom-end"
                                    sx={styles.popper(theme)}
                                >
                                    <ClickAwayListener onClickAway={closePopper}>
                                        <div>{p.element}</div>
                                    </ClickAwayListener>
                                </Popper>
                            </Fragment>
                        ))}
                    </Box>
                </Box>
            </Box>
        </AppBar>
    );
};

export default AppToolbar;
